(() => {
  'use strict';

  const selector = '[data-aurara-recipe-article-hero]';
  const round = (value) => Math.round(value);
  const energyRangeForWeight = (weight) => {
    const kilograms = weight / 2.20462;
    const rer = Math.pow(kilograms, 0.75);
    const low = round(95 * rer);
    const high = round(130 * rer);
    return { low, high, mid: round((low + high) / 2) };
  };
  const parseIngredientWeight = (value) => {
    const match = String(value || '').trim().match(/^([\d.]+)\s*(g|gram|grams|oz|ounce|ounces)\b/i);
    if (!match) return 0;
    const amount = Number(match[1]) || 0;
    return /^oz|ounce/i.test(match[2]) ? amount * 28.3495 : amount;
  };
  const replaceCookTime = (copy, cookTime) => copy
    .replace(/\[cook_time\]/gi, cookTime)
    // Support existing recipe text such as "cook for 60 minutes", not only [cook_time].
    .replace(/\b(?:30|45|60|90)\s*(?:minutes?|mins?)\b/gi, cookTime);

  const init = (scope = document) => {
    const roots = [];
    if (scope instanceof Element && scope.matches(selector)) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll(selector));

    roots.forEach((root) => {
      if (root.dataset.recipeInitialized === 'true') return;
      root.dataset.recipeInitialized = 'true';

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const reveal = () => root.querySelectorAll('[data-recipe-reveal]').forEach((element) => element.classList.add('is-revealed'));
      if (root.dataset.recipeAnimations === 'false' || reduced || !('IntersectionObserver' in window)) {
        reveal();
      } else {
        root.classList.add('is-enhanced');
        const observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            reveal();
            observer.disconnect();
          }
        }, { rootMargin: '0px 0px -15% 0px' });
        observer.observe(root);
        window.setTimeout(reveal, 1500);
      }

      const calculator = root.querySelector('[data-recipe-calculator]');
      const prepLegend = root.querySelector('[data-recipe-prep-legend]');
      root.querySelectorAll('[data-recipe-details-ingredient] em').forEach((chip) => {
        const prep = chip.textContent.trim().toLowerCase();
        chip.classList.toggle('is-after', prep === 'add after cooking');
        chip.classList.toggle('is-precook', ['pre-cook', 'pre cook', 'precook'].includes(prep));
      });
      if (prepLegend) {
        const hints = {
          'add directly': 'Drop in as-is — meats, root veg, greens',
          'pre-soak': 'Soak before cooking — rice, quinoa',
          'pre-cook': 'Partially cook first — peas, oats, mixed grains',
          'add after cooking': 'Stir in once done — berries, supplements'
        };
        const labels = [...new Set([...root.querySelectorAll('[data-recipe-details-ingredient] em')]
          .map((element) => element.textContent.trim())
          .filter(Boolean))];
        if (labels.length) {
          prepLegend.replaceChildren(...labels.map((label) => {
            const item = document.createElement('span');
            const chip = document.createElement('em');
            const hint = document.createElement('small');
            chip.textContent = label;
            const prep = label.toLowerCase();
            chip.classList.toggle('is-after', prep === 'add after cooking');
            chip.classList.toggle('is-precook', ['pre-cook', 'pre cook', 'precook'].includes(prep));
            hint.textContent = hints[prep] || '';
            item.append(chip, hint);
            return item;
          }));
        }
      }
      const baseIngredientWeight = [...root.querySelectorAll('[data-recipe-details-ingredient]')]
        .reduce((total, item) => total + parseIngredientWeight(item.dataset.amount), 0);
      const timeBadge = root.querySelector('[data-recipe-cook-time]');
      const timeRuleEnabled = root.dataset.recipeTimeRule === 'true';
      const timeThreshold = Number(root.dataset.recipeTimeThreshold) || 500;
      const timeBelow = root.dataset.recipeTimeBelow || '30 minutes';
      const timeAbove = root.dataset.recipeTimeAbove || '60 minutes';
      const updateMethodTime = (factor = 1) => {
        if (!timeRuleEnabled) return;
        const ingredientWeight = baseIngredientWeight * (Number(factor) || 1);
        const cookTime = ingredientWeight < timeThreshold ? timeBelow : timeAbove;
        if (timeBadge) timeBadge.textContent = cookTime;
        root.querySelectorAll('[data-recipe-method-copy]').forEach((copy) => {
          if (copy.dataset.recipeOriginalCopy === undefined) copy.dataset.recipeOriginalCopy = copy.textContent;
          copy.textContent = replaceCookTime(copy.dataset.recipeOriginalCopy, cookTime);
        });
      };
      updateMethodTime();
      if (!calculator) return;

      let mode = 'weight';
      const base = Number(root.dataset.recipeBaseKcal) || 0;
      const weight = calculator.querySelector('[data-recipe-weight]');
      const energy = calculator.querySelector('[data-recipe-energy]');
      const days = calculator.querySelector('[data-recipe-days]');
      const batch = calculator.querySelector('[data-recipe-batch]');
      const weightField = calculator.querySelector('[data-recipe-weight-field]');
      const note = calculator.querySelector('[data-recipe-note]');

      const update = () => {
        const dogWeight = Number(weight?.value) || 0;
        const range = mode === 'weight' && dogWeight > 0 ? energyRangeForWeight(dogWeight) : null;
        const daily = mode === 'weight' ? (range?.mid || 0) : Number(energy?.value) || 0;
        const dayCount = Math.max(1, Math.floor(Number(days?.value) || 1));
        const targetKcal = daily ? round(daily * dayCount) : 0;
        const factor = daily && base ? targetKcal / base : 0;
        const portion = { targetKcal, factor };
        updateMethodTime(factor || 1);

        window.AuraraRecipePortion = portion;
        window.dispatchEvent(new CustomEvent('aurara:recipe-portion', { detail: portion }));

        if (mode === 'weight' && energy) energy.value = daily ? String(daily) : '';
        if (batch) batch.innerHTML = `${targetKcal} <small>kcal</small>`;
        if (note) {
          const guidance = range
            ? `WSAVA guideline for a healthy adult dog: <strong>${range.low}–${range.high} kcal/day</strong> (inactive → active); the midpoint is used above. Adjust in Custom Energy if your vet recommends otherwise.`
            : mode === 'custom'
              ? 'Know your dog’s daily target (e.g. from your vet)? Enter it directly.'
              : 'Enter your dog’s weight — we’ll estimate daily energy with the WSAVA adult-dog formula.';
          const baseRecipe = base
            ? `Base recipe = <strong>${base} kcal</strong> as written — ingredient amounts below scale to match your batch.`
            : '';
          note.innerHTML = `<p>${guidance}</p>${baseRecipe ? `<p>${baseRecipe}</p>` : ''}`;
        }
      };

      calculator.querySelectorAll('[data-recipe-mode]').forEach((button) => button.addEventListener('click', () => {
        mode = button.dataset.recipeMode || 'weight';
        calculator.querySelectorAll('[data-recipe-mode]').forEach((tab) => {
          const active = tab === button;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
        });
        if (weightField) weightField.hidden = mode !== 'weight';
        if (energy) {
          energy.readOnly = mode === 'weight';
          energy.placeholder = mode === 'weight' ? '—' : '1131';
        }
        update();
      }));

      weight?.addEventListener('input', update);
      energy?.addEventListener('input', update);
      days?.addEventListener('input', update);
      update();
    });
  };

  const destroy = (scope) => {
    const roots = [];
    if (scope instanceof Element && scope.matches(selector)) roots.push(scope);
    if (scope?.querySelectorAll) roots.push(...scope.querySelectorAll(selector));
    roots.forEach((root) => delete root.dataset.recipeInitialized);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
